import { useEffect, useState } from 'react';
import { Button, Textfield } from '@flexo/atoms';
import {
  FirebaseService,
  IconWrapper,
  REGEX,
  httpService,
} from '@flexo/general';
import styles from './web-log-in.module.scss';
import { Player } from '@lottiefiles/react-lottie-player';
import { useTranslation } from 'react-i18next';
import { ALERT, USER } from '@flexo/reducers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const ENV = (import.meta as any).env;
const firebaseService = new FirebaseService();

/* eslint-disable-next-line */
export interface WebLogInProps {
  logInData: any;
  setLogInFlow: any;
  bgColor?: string;
}

export function WebLogIn({ setLogInFlow }: WebLogInProps) {
  const { LogIn__Inputs, LogIn__Footer, LogIn__Footer__Button } = styles;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    createPassword: '',
  });

  const [logInClicked, setLogInClicked] = useState(false);
  const [logInError, setLogInError] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(false);
  const { t } = useTranslation();

  const [inputType, setInputType] = useState({
    email: 'email',
    password: 'password',
    confirmPassword: 'password',
  });

  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });

  const { title, inputs, buttonLabel } = t('pages.login.data', {
    returnObjects: true,
  }) as any;

  const loader = '/loaderLottie.json';

  // Check if user was redirected after password update
  useEffect(() => {
    const passwordUpdatedLocal = localStorage.getItem('passwordUpdated');
    const passwordUpdatedSession = sessionStorage.getItem('passwordUpdated');

    if (passwordUpdatedLocal === 'true' || passwordUpdatedSession === 'true') {
      localStorage.removeItem('passwordUpdated');
      sessionStorage.removeItem('passwordUpdated');

      dispatch(ALERT.ResetAlert());
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_PASSWORD_RESET_SUCCESS',
          },
        } as any)
      );

      console.log('Password update success banner shown');
    }
  }, [dispatch]);

  const handleInputChange = (event: any) => {
    setAuthData({ ...authData, [event.target.name]: event.target.value });
  };

  const signIn = async () => {
    if (logInClicked) return;

    setLogInClicked(true);
    setLogInError(false);

    try {
      

      // First, get the user token.
      const response: any = await httpService.post({
        url: `${ENV.VITE_APP_FIREBASE_CUSTOM_TOKEN}`,
        data: {
          email: authData.email,
          password: authData.password,
        },
      });

      // Use the token from the response to sign in.
      const firebaseResult = await firebaseService.signInWithToken(
        response?.data?.token
      );

      // Check user role for web admin access
      try {
        const userRole = JSON.parse(
          (firebaseResult.user as any)?.reloadUserInfo?.customAttributes
        );

        if (
          userRole?.user_role !== 'administrator' &&
          userRole?.user_role !== 'supervisor'
        ) {
          dispatch(
            ALERT.SetAlert({
              payload: {
                value: {
                  type: 'BANNER_NOT_ALLOWED',
                },
              },
            } as any)
          );

          firebaseService.signOut();
          setAuthData({
            email: '',
            password: '',
            createPassword: '',
          });
          return;
        }
      } catch (error) {
        console.warn('Error parsing user role:', error);
      }

      dispatch(USER.SetUserAsync(firebaseResult as any) as any);

      return firebaseResult;
    } catch (error: any) {
      if (error.message === 'ERR_NETWORK') {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_WRONG_LOGIN',
              },
            },
          } as any)
        );
      }

      setAuthData({ ...authData, password: '' });
      setLogInError(true);
      

      // console.error(error);
    } finally {
      setLogInClicked(false);
    }
  };

  const togglePasswordVisibility = () => {
    setInputType((prevState) => ({
      ...prevState,
      password: prevState.password === 'password' ? 'text' : 'password',
    }));
  };

  const handleDescriptionClick = (inputName: string) => {
    if (inputName === 'password') {
      navigate('/forgot-password');
    }
  };

  const isValidEmail = (email: string) => {
    return REGEX.email.test(email);
  };

  const handleBlur = (event: any) => {
    const { name, value } = event.target;
    if (name === 'email' && value.length) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: !isValidEmail(value),
      }));
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      if (
        authData.password !== '' &&
        isValidEmail(authData.email) &&
        !logInClicked &&
        !logInError
      ) {
        signIn();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [authData, logInClicked, logInError]);

  return (
    <div className={styles.LogIn}>
      <div className={styles.LogIn__Logo}>
        <IconWrapper iconName="logo_wide" />
      </div>
      <div className={styles.Login__Content}>
        <div className={styles.LogIn__Header__Title}>{title}</div>

        <div className={LogIn__Inputs}>
          {inputs?.map((input: any, input_i: number) => (
            <div key={input.name} className={ input_i < inputs.length - 1 ? 'mb-2' : ''}>
              <Textfield
                textfieldColor='grey'
                variant="default"
                name={input.name}
                value={authData[input.name]}
                placeholder={input.placeholder}
                label={input.label}
                type={inputType[input.name]}
                error={errors[input.name] || logInError}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onPaste={(e) => false}
                onCopy={(e) => false}
                description={input.description}
                onDescriptionClick={() => handleDescriptionClick(input.name)}
                errorDescription={
                  input.name === 'password' && logInError ? input.descriptionError : ''
                }
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                icon={
                  input.name === 'password'
                    ? inputType.password === 'password'
                      ? 'show'
                      : 'hide'
                    : ''
                }
                onIconClick={
                  input.name === 'password'
                    ? togglePasswordVisibility
                    : undefined
                }
                onFocus={() => {
                  setLogInError(false);
                  setErrors({ email: false, password: false });
                }}
              />
            </div>
          ))}
        </div>

        <div className={LogIn__Footer}>
          <Button
            className={LogIn__Footer__Button}
            onClick={signIn}
            label={
              logInClicked && !logInError ? (
                <Player
                  autoplay
                  loop
                  src={loader}
                  style={{ height: 20, transform: 'scale(0.7)' }}
                />
              ) : (
                buttonLabel
              )
            }
            variant="primary"
            color="secondary"
            disabled={
              authData.password === '' ||
              !isValidEmail(authData.email) ||
              (logInClicked && !logInError) ||
              networkStatus ||
              errors.email
            }
          />
        </div>
      </div>
    </div>
  );
}

export default WebLogIn;
